import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Download,
  Loader2,
  RefreshCw,
  Search,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useViewModelReady } from '../../hooks/useViewModel';
import { AdminLayout, adminToolPath } from '../../components/AdminLayout';
import { Tabs, TabDef } from '../../components/Tabs';
import { CampaignProgressBar } from '../../components/CampaignProgressBar';
import { SearchableDropdown } from '../../components/SearchableDropdown';
import { TaskTypeEditor } from '../AdminPage/tools/CampaignsTool/TaskTypeEditor';
import { ExportTasksModal } from '../AdminPage/tools/CampaignsTool/ExportTasksModal';
import AssignTaskModal, { AssignTaskRequest } from './AssignTaskModal';
import {
  CampaignUserTaskStats,
  totalCampaignUserTaskStats,
} from '../AdminPage/tools/CampaignsTool/userStats';
import { buildBrowseTasksSearchParams } from '../BrowseTasksPage/filter';
import { paths } from '../../routes/paths';
import { Campaign, User } from '../../types';
import { AiDetectionWarningPill } from '../../components/AiDetectionWarningPill';
import { getTaskTypeDescriptor } from '../../taskTypes/descriptor';
import { useAdminCampaignDetailViewModel } from './viewModel';
import { ActivityPanel } from './ActivityPanel';
import { ExpertSort, ExpertSortKey, sortExperts } from './expertSort';
import {
  AUTO_RELEASE_STALE_CLAIMS_MAX_DAYS,
  AUTO_RELEASE_STALE_CLAIMS_MIN_DAYS,
  AUTO_RELEASE_STALE_IN_PROGRESS_MAX_DAYS,
  AUTO_RELEASE_STALE_IN_PROGRESS_MIN_DAYS,
  AUTO_RELEASE_STALE_REVIEWS_MIN_HOURS,
  AUTO_RELEASE_STALE_REVIEWS_MAX_HOURS,
  MAX_CLAIMED_TASKS_PER_USER_LIMIT,
  MAX_TASKS_PER_USER_LIMIT,
} from '../../services/adminCampaigns';

export function AdminCampaignDetailPage() {
  const { isAdmin } = useAuth();
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const vm = useAdminCampaignDetailViewModel(campaignId ?? '');
  const { initialize, dispose } = vm;
  useViewModelReady(initialize);
  useEffect(() => () => dispose(), [dispose]);

  if (!isAdmin) {
    return <Navigate to={paths.queue()} replace />;
  }
  if (!campaignId) {
    return <Navigate to={adminToolPath('campaigns')} replace />;
  }

  return (
    <AdminLayout
      activeId="campaigns"
      onSelectTool={(id) => navigate(adminToolPath(id))}
      backLabel="Back to admin"
      onBack={() => navigate(adminToolPath('campaigns'))}
    >
      {({ mobileNavTrigger }) => (
        <DetailContent vm={vm} mobileNavTrigger={mobileNavTrigger} />
      )}
    </AdminLayout>
  );
}

function DetailContent({
  vm,
  mobileNavTrigger,
}: {
  vm: ReturnType<typeof useAdminCampaignDetailViewModel>;
  mobileNavTrigger: React.ReactNode;
}) {
  const [exportOpen, setExportOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CampaignDetailTabId>('task-types');
  const [taskTypeSettingsTab, setTaskTypeSettingsTab] = useState('');

  if (vm.busy) {
    return (
      <section className="min-w-0">
        <PageHeading mobileNavTrigger={mobileNavTrigger} />
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  if (vm.notFound || !vm.campaign) {
    return (
      <section className="min-w-0">
        <PageHeading mobileNavTrigger={mobileNavTrigger} />
        <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          Campaign not found. It may have been deleted or you may have followed a stale link.
          <div className="mt-3">
            <Link
              to={adminToolPath('campaigns')}
              className="text-sm font-medium text-primary hover:text-primary-hover"
            >
              ← Back to campaigns
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const campaign = vm.campaign;

  return (
    <section className="min-w-0 space-y-6">
      <PageHeading
        mobileNavTrigger={mobileNavTrigger}
        campaign={campaign}
        onOpenExport={() => setExportOpen(true)}
        onReload={() => void vm.reloadCounts()}
        reloading={vm.countsLoading || vm.userStatsLoading}
        exporting={vm.exporting}
      />

      {vm.error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {vm.error}
        </div>
      )}

      <OverallProgressCard
        counts={vm.counts}
        loading={vm.countsLoading}
        matchedExpertCount={countMatchedActiveExperts(campaign, vm.usersByExpertId)}
        totalExpertCount={campaign.activeExpertIds.length}
      />

      <CampaignDetailTabs activeId={activeTab} onChange={setActiveTab} />

      <div className="mt-6">
        {activeTab === 'task-types' && (
          <TaskTypesPanel
            campaign={campaign}
            vm={vm}
          />
        )}

        {activeTab === 'experts' && (
          <ExpertList
            campaign={campaign}
            matchedUsers={vm.matchedUsers}
            filteredUsers={vm.filteredUsers}
            expertSort={vm.expertSort}
            onSort={vm.sortExpertsBy}
            userQuery={vm.userQuery}
            onUserQueryChange={vm.setUserQuery}
            userStats={vm.userStats}
            userStatsLoading={vm.userStatsLoading}
            onAssign={vm.assignTask}
            pendingAssign={vm.pendingAssign}
          />
        )}

        {activeTab === 'activity' && (
          <ActivityPanel
            campaignId={campaign.id}
            campaignName={campaign.name}
            users={vm.activityUsers}
            writerIds={vm.matchedUsers.map((user) => user.uid)}
          />
        )}

        {activeTab === 'settings' && (
          <CampaignSettingsPanel
            campaign={campaign}
            vm={vm}
            taskTypeSettingsTab={taskTypeSettingsTab}
            onTaskTypeSettingsTabChange={setTaskTypeSettingsTab}
          />
        )}
      </div>

      {exportOpen && (
        <ExportTasksModal
          campaignName={campaign.name || '(no name)'}
          campaignTaskTypes={campaign.taskTypes}
          exporting={vm.exporting}
          onClose={() => setExportOpen(false)}
          onExport={vm.exportTasks}
        />
      )}
    </section>
  );
}

type CampaignDetailTabId = 'task-types' | 'experts' | 'activity' | 'settings';

const CAMPAIGN_DETAIL_TABS: TabDef[] = [
  { id: 'task-types', label: 'Task Types' },
  { id: 'experts', label: 'Experts' },
  { id: 'activity', label: 'Activity' },
  { id: 'settings', label: 'Settings' },
];

function CampaignDetailTabs({
  activeId,
  onChange,
}: {
  activeId: CampaignDetailTabId;
  onChange: (id: CampaignDetailTabId) => void;
}) {
  return (
    <Tabs
      tabs={CAMPAIGN_DETAIL_TABS}
      activeId={activeId}
      onChange={(id) => onChange(id as CampaignDetailTabId)}
    />
  );
}

function TaskTypesPanel({
  campaign,
  vm,
}: {
  campaign: Campaign;
  vm: ReturnType<typeof useAdminCampaignDetailViewModel>;
}) {
  return (
    <section>
      <SectionHeader
        title="Task types"
        subtitle="Review task pools and add or remove task types. Configure target pools and per-type options in Settings."
      />
      <div className="mt-3">
        <TaskTypeEditor
          campaign={campaign}
          countsByType={vm.countsByType}
          countsLoading={vm.countsLoading}
          pending={vm.pendingAdjust}
          showProgressBar={true}
          onApply={vm.adjust}
          onPreview={vm.previewAdjust}
          onSetDomains={vm.setDomains}
          onToggleLlmReview={vm.setLlmReview}
          onToggleLlmValidator={vm.setLlmValidator}
          onSetClaimingPaused={vm.setClaimingPaused}
          onReloadCounts={vm.reloadCounts}
        />
      </div>
    </section>
  );
}

function CampaignSettingsPanel({
  campaign,
  vm,
  taskTypeSettingsTab,
  onTaskTypeSettingsTabChange,
}: {
  campaign: Campaign;
  vm: ReturnType<typeof useAdminCampaignDetailViewModel>;
  taskTypeSettingsTab: string;
  onTaskTypeSettingsTabChange: (id: string) => void;
}) {
  const [pausePending, setPausePending] = useState(false);
  const [allowedTasksPending, setAllowedTasksPending] = useState(false);
  const savedAllowedTasks = (campaign.pauseAllowedTaskIds ?? []).join('\n');
  const [allowedTasks, setAllowedTasks] = useState(savedAllowedTasks);
  useEffect(() => setAllowedTasks(savedAllowedTasks), [campaign.id, savedAllowedTasks]);
  const taskTypeTabs = campaign.taskTypes.map((entry) => ({
    id: entry.taskTypeId,
    label: getTaskTypeDescriptor(entry.taskTypeId).shortName,
  }));
  const selectedTaskTypeId = campaign.taskTypes.some(
    (entry) => entry.taskTypeId === taskTypeSettingsTab,
  )
    ? taskTypeSettingsTab
    : campaign.taskTypes[0]?.taskTypeId ?? '';

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <span>
            <span className="block text-base font-semibold text-gray-900">Pause Campaign</span>
            <span className="mt-1 block text-sm text-gray-500">
              Show a paused notice on queues and task pages, except for allowed tasks below. Unpause to restore normal access.
            </span>
          </span>
          <button
            type="button"
            disabled={pausePending}
            className="shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={async () => {
              const paused = !campaign.paused;
              setPausePending(true);
              try {
                await vm.setPaused(paused);
              } catch {
                // The view model presents and logs the error.
              } finally {
                setPausePending(false);
              }
            }}
          >
            {campaign.paused ? 'Unpause' : 'Pause'}
          </button>
        </div>
        {campaign.paused && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <label htmlFor="pause-allowed-tasks" className="block text-sm font-medium text-gray-900">
              Tasks allowed while paused
            </label>
            <p id="pause-allowed-tasks-help" className="mt-1 text-sm text-gray-500">
              Enter task IDs separated by commas or new lines. Owners can open and submit these tasks, and admins can review them. Everyone else sees the paused notice. Open allowed tasks using their task links.
            </p>
            <textarea
              id="pause-allowed-tasks"
              aria-describedby="pause-allowed-tasks-help"
              rows={4}
              value={allowedTasks}
              disabled={allowedTasksPending}
              onChange={(event) => setAllowedTasks(event.target.value)}
              className="mt-2 block w-full rounded-md border border-gray-300 p-2 text-sm"
            />
            <button
              type="button"
              disabled={allowedTasksPending || allowedTasks === savedAllowedTasks}
              className="mt-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={async () => {
                setAllowedTasksPending(true);
                try {
                  await vm.setPauseAllowedTaskIds(Array.from(new Set(allowedTasks.split(/[,\n]/).map((id) => id.trim()).filter(Boolean))));
                } catch {
                  // The view model presents and logs the error.
                } finally {
                  setAllowedTasksPending(false);
                }
              }}
            >
              {allowedTasksPending ? 'Saving…' : 'Save allowed tasks'}
            </button>
          </div>
        )}
      </section>
      <section>
        <SectionHeader title="Campaign settings" subtitle="Control how experts pick up new work." />
        <div className="mt-3">
          <ClaimSettingsCard
            campaign={campaign}
            adminUsers={vm.adminUsers}
            trustedUsers={vm.trustedUsers}
            onToggleFirstApprovalGate={vm.setRequireFirstApprovalBeforeMoreClaims}
            onSetAutoApprove={vm.setAutoApprove}
            onSetSubmissionEmail={vm.setSubmissionEmail}
            onSetEscalationAdmin={vm.setEscalationAdmin}
            onSetAutoReleaseStaleClaims={vm.setAutoReleaseStaleClaims}
            onSetAutoReleaseStaleInProgress={vm.setAutoReleaseStaleInProgress}
            onSetAutoReleaseStaleReviews={vm.setAutoReleaseStaleReviews}
            onSetMaxTasksPerUser={vm.setMaxTasksPerUser}
            onSetMaxTasksPerTrustedUser={vm.setMaxTasksPerTrustedUser}
            onSetMaxClaimedTasksPerUser={vm.setMaxClaimedTasksPerUser}
          />
        </div>
      </section>

      <section>
        <SectionHeader
          title="Task type settings"
          subtitle="Configure the options that apply to each task type in this campaign."
        />
        {taskTypeTabs.length === 0 ? (
          <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
            Add a task type from the Task Types tab to configure its settings.
          </div>
        ) : (
          <div
            data-testid="task-type-settings-card"
            className="mt-3 rounded-lg border border-gray-200 bg-white p-4 sm:p-5"
          >
            <Tabs
              tabs={taskTypeTabs}
              activeId={selectedTaskTypeId}
              onChange={onTaskTypeSettingsTabChange}
            />
            <div className="mt-3">
              <TaskTypeEditor
                campaign={campaign}
                taskTypeIds={[selectedTaskTypeId]}
                mode="settings"
                countsByType={vm.countsByType}
                countsLoading={vm.countsLoading}
                pending={vm.pendingAdjust}
                onApply={vm.adjust}
                onPreview={vm.previewAdjust}
                onSetDomains={vm.setDomains}
                onToggleLlmReview={vm.setLlmReview}
                onToggleLlmValidator={vm.setLlmValidator}
                onSetClaimingPaused={vm.setClaimingPaused}
                onReloadCounts={vm.reloadCounts}
              />
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function PageHeading({
  mobileNavTrigger,
  campaign,
  onOpenExport,
  onReload,
  reloading,
  exporting,
}: {
  mobileNavTrigger: React.ReactNode;
  campaign?: Campaign;
  onOpenExport?: () => void;
  onReload?: () => void;
  reloading?: boolean;
  exporting?: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={adminToolPath('campaigns')}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to campaigns
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              {campaign?.name || (campaign ? '(no name)' : 'Campaign')}
            </h1>
            {campaign?.archivedAt && (
              <span className="rounded bg-gray-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-700">
                Archived
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {campaign && onReload && (
            <button
              type="button"
              onClick={onReload}
              disabled={reloading}
              title="Reload counts"
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {reloading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Reload
            </button>
          )}
          {campaign && onOpenExport && (
            <button
              type="button"
              onClick={onOpenExport}
              disabled={exporting || campaign.taskTypes.length === 0}
              title={
                campaign.taskTypes.length === 0
                  ? 'Bind a task type before exporting'
                  : 'Export tasks'
              }
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-white hover:bg-primary-hover disabled:opacity-50"
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              Export tasks…
            </button>
          )}
          {mobileNavTrigger}
        </div>
      </div>
      {campaign?.description && (
        <p className="w-full text-sm text-gray-500">{campaign.description}</p>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h2>
      {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
}

function OverallProgressCard({
  counts,
  loading,
  matchedExpertCount,
  totalExpertCount,
}: {
  counts: ReturnType<typeof useAdminCampaignDetailViewModel>['counts'];
  loading: boolean;
  matchedExpertCount: number;
  totalExpertCount: number;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-9">
        <Stat label="Total tasks" value={counts?.total} loading={loading} accent="text-gray-900" />
        <Stat label="Completed" value={counts?.approved} loading={loading} accent="text-emerald-600" />
        <Stat label="In review" value={counts?.inReview} loading={loading} accent="text-blue-600" />
        <Stat label="Rework" value={counts?.rework} loading={loading} accent="text-rose-600" />
        <Stat label="In progress" value={counts?.inProgress} loading={loading} accent="text-violet-600" />
        <Stat label="Claimed" value={counts?.claimed} loading={loading} accent="text-amber-600" />
        <Stat label="Available" value={counts?.available} loading={loading} accent="text-gray-700" />
        <Stat label="Archived" value={counts?.archived} loading={loading} accent="text-gray-500" />
        <Stat
          label="Active experts"
          valueText={`${matchedExpertCount} / ${totalExpertCount}`}
          loading={false}
          accent="text-gray-900"
          icon={<Users className="h-3.5 w-3.5 text-gray-400" />}
        />
      </div>
      <div className="mt-3">
        <CampaignProgressBar counts={counts} loading={loading} />
      </div>
    </div>
  );
}

function countMatchedActiveExperts(
  campaign: Campaign,
  usersByExpertId: Map<string, User>,
): number {
  return campaign.activeExpertIds.filter((expertId) => usersByExpertId.has(expertId)).length;
}

function ClaimSettingsCard({
  campaign,
  adminUsers,
  trustedUsers,
  onToggleFirstApprovalGate,
  onSetAutoApprove,
  onSetSubmissionEmail,
  onSetEscalationAdmin,
  onSetAutoReleaseStaleClaims,
  onSetAutoReleaseStaleInProgress,
  onSetAutoReleaseStaleReviews,
  onSetMaxTasksPerUser,
  onSetMaxTasksPerTrustedUser,
  onSetMaxClaimedTasksPerUser,
}: {
  campaign: Campaign;
  adminUsers: User[];
  trustedUsers: User[];
  onToggleFirstApprovalGate: (enabled: boolean) => Promise<void>;
  onSetAutoApprove: (enabled: boolean) => Promise<void>;
  onSetSubmissionEmail: (enabled: boolean) => Promise<void>;
  onSetEscalationAdmin: (adminId: string | null) => Promise<void>;
  onSetAutoReleaseStaleClaims: (enabled: boolean, days: number) => Promise<void>;
  onSetAutoReleaseStaleInProgress: (enabled: boolean, days: number) => Promise<void>;
  onSetAutoReleaseStaleReviews: (enabled: boolean, hours: number) => Promise<void>;
  onSetMaxTasksPerUser: (maxTasksPerUser: number | null) => Promise<void>;
  onSetMaxTasksPerTrustedUser: (value: number | null) => Promise<void>;
  onSetMaxClaimedTasksPerUser: (maxClaimedTasksPerUser: number | null) => Promise<void>;
}) {
  const [pending, setPending] = useState(false);
  const [autoApprovePending, setAutoApprovePending] = useState(false);
  const [submissionEmailPending, setSubmissionEmailPending] = useState(false);
  const [escalationPending, setEscalationPending] = useState(false);
  const [releasePending, setReleasePending] = useState(false);
  const [inProgressReleasePending, setInProgressReleasePending] = useState(false);
  const [reviewReleasePending, setReviewReleasePending] = useState(false);
  const [reviewHoursDraft, setReviewHoursDraft] = useState(String(campaign.autoReleaseStaleReviews?.hours ?? 4));
  const [maxTasksPending, setMaxTasksPending] = useState(false);
  const [maxTrustedTasksPending, setMaxTrustedTasksPending] = useState(false);
  const [maxClaimedTasksPending, setMaxClaimedTasksPending] = useState(false);
  const [daysDraft, setDaysDraft] = useState(
    String(campaign.autoReleaseStaleClaims?.days ?? 7),
  );
  const [inProgressDaysDraft, setInProgressDaysDraft] = useState(
    String(campaign.autoReleaseStaleInProgress?.days ?? 7),
  );
  const [maxTasksDraft, setMaxTasksDraft] = useState(
    campaign.maxTasksPerUser == null ? '' : String(campaign.maxTasksPerUser),
  );

  const [maxTrustedTasksDraft, setMaxTrustedTasksDraft] = useState(
    campaign.maxTasksPerTrustedUser == null ? '' : String(campaign.maxTasksPerTrustedUser),
  );
  const [maxClaimedTasksDraft, setMaxClaimedTasksDraft] = useState(
    campaign.maxClaimedTasksPerUser == null ? '' : String(campaign.maxClaimedTasksPerUser),
  );
  const enabled = campaign.requireFirstApprovalBeforeMoreClaims;
  const releaseEnabled = campaign.autoReleaseStaleClaims?.enabled === true;
  const releaseDays = campaign.autoReleaseStaleClaims?.days ?? 7;
  const inProgressReleaseEnabled = campaign.autoReleaseStaleInProgress?.enabled === true;
  const inProgressReleaseDays = campaign.autoReleaseStaleInProgress?.days ?? 7;
  const reviewReleaseEnabled = campaign.autoReleaseStaleReviews?.enabled === true;
  const reviewReleaseHours = campaign.autoReleaseStaleReviews?.hours ?? 4;

  useEffect(() => { setReviewHoursDraft(String(reviewReleaseHours)); }, [reviewReleaseHours]);
  const autoApproveEnabled = campaign.autoApprove?.enabled === true;
  const submissionEmailEnabled = campaign.submissionEmail?.enabled === true;

  useEffect(() => {
    setDaysDraft(String(releaseDays));
  }, [releaseDays]);

  useEffect(() => {
    setInProgressDaysDraft(String(inProgressReleaseDays));
  }, [inProgressReleaseDays]);

  useEffect(() => {
    setMaxTasksDraft(campaign.maxTasksPerUser == null ? '' : String(campaign.maxTasksPerUser));
  }, [campaign.maxTasksPerUser]);
  useEffect(() => {
    setMaxTrustedTasksDraft(campaign.maxTasksPerTrustedUser == null ? '' : String(campaign.maxTasksPerTrustedUser));
  }, [campaign.maxTasksPerTrustedUser]);

  useEffect(() => {
    setMaxClaimedTasksDraft(
      campaign.maxClaimedTasksPerUser == null ? '' : String(campaign.maxClaimedTasksPerUser),
    );
  }, [campaign.maxClaimedTasksPerUser]);

  const toggle = async () => {
    setPending(true);
    try {
      await onToggleFirstApprovalGate(!enabled);
    } finally {
      setPending(false);
    }
  };

  const commitEscalationAdmin = async (adminId: string) => {
    setEscalationPending(true);
    try {
      await onSetEscalationAdmin(adminId || null);
    } catch {
      // The view model presents and logs the error.
    } finally {
      setEscalationPending(false);
    }
  };

  const toggleAutoApprove = async () => {
    setAutoApprovePending(true);
    try {
      await onSetAutoApprove(!autoApproveEnabled);
    } catch {
      // The view model presents and logs the error.
    } finally {
      setAutoApprovePending(false);
    }
  };

  const toggleSubmissionEmail = async () => {
    setSubmissionEmailPending(true);
    try {
      await onSetSubmissionEmail(!submissionEmailEnabled);
    } catch {
      // The view model presents and logs the error.
    } finally {
      setSubmissionEmailPending(false);
    }
  };

  const commitAutoRelease = async (nextEnabled: boolean, rawDays = daysDraft) => {
    const parsed = Number(rawDays);
    const cleaned = Number.isFinite(parsed) ? Math.trunc(parsed) : releaseDays;
    const bounded = Math.min(
      AUTO_RELEASE_STALE_CLAIMS_MAX_DAYS,
      Math.max(AUTO_RELEASE_STALE_CLAIMS_MIN_DAYS, cleaned),
    );
    setDaysDraft(String(bounded));
    setReleasePending(true);
    try {
      await onSetAutoReleaseStaleClaims(nextEnabled, bounded);
    } finally {
      setReleasePending(false);
    }
  };

  const commitAutoReleaseInProgress = async (
    nextEnabled: boolean,
    rawDays = inProgressDaysDraft,
  ) => {
    const parsed = Number(rawDays);
    const cleaned = Number.isFinite(parsed) ? Math.trunc(parsed) : inProgressReleaseDays;
    const bounded = Math.min(
      AUTO_RELEASE_STALE_IN_PROGRESS_MAX_DAYS,
      Math.max(AUTO_RELEASE_STALE_IN_PROGRESS_MIN_DAYS, cleaned),
    );
    setInProgressDaysDraft(String(bounded));
    setInProgressReleasePending(true);
    try {
      await onSetAutoReleaseStaleInProgress(nextEnabled, bounded);
    } finally {
      setInProgressReleasePending(false);
    }
  };

  const commitAutoReleaseReviews = async (
    nextEnabled: boolean,
    rawHours = reviewHoursDraft,
  ) => {
    const parsed = Number(rawHours);
    const cleaned = Number.isFinite(parsed) ? Math.trunc(parsed) : reviewReleaseHours;
    const bounded = Math.min(
      AUTO_RELEASE_STALE_REVIEWS_MAX_HOURS,
      Math.max(AUTO_RELEASE_STALE_REVIEWS_MIN_HOURS, cleaned),
    );
    setReviewHoursDraft(String(bounded));
    setReviewReleasePending(true);
    try {
      await onSetAutoReleaseStaleReviews(nextEnabled, bounded);
    } catch {
      // The view model presents and logs the error.
    } finally {
      setReviewReleasePending(false);
    }
  };

  const commitMaxTasksPerUser = async (rawValue = maxTasksDraft) => {
    const trimmed = rawValue.trim();
    const parsed = Number(trimmed);
    const nextValue = trimmed === '' || !Number.isFinite(parsed) ? null : parsed;
    const bounded = nextValue == null
      ? null
      : Math.min(MAX_TASKS_PER_USER_LIMIT, Math.max(1, Math.trunc(nextValue)));
    setMaxTasksDraft(bounded == null ? '' : String(bounded));
    setMaxTasksPending(true);
    try {
      await onSetMaxTasksPerUser(bounded);
    } finally {
      setMaxTasksPending(false);
    }
  };

  const commitMaxTasksPerTrustedUser = async (rawValue = maxTrustedTasksDraft) => {
    const trimmed = rawValue.trim();
    const parsed = Number(trimmed);
    const nextValue = trimmed === '' || !Number.isFinite(parsed) ? null : parsed;
    const bounded = nextValue == null
      ? null
      : Math.min(MAX_TASKS_PER_USER_LIMIT, Math.max(1, Math.trunc(nextValue)));
    setMaxTrustedTasksDraft(bounded == null ? '' : String(bounded));
    setMaxTrustedTasksPending(true);
    try {
      await onSetMaxTasksPerTrustedUser(bounded);
    } finally {
      setMaxTrustedTasksPending(false);
    }
  };

  const commitMaxClaimedTasksPerUser = async (rawValue = maxClaimedTasksDraft) => {
    const trimmed = rawValue.trim();
    const parsed = Number(trimmed);
    const nextValue = trimmed === '' || !Number.isFinite(parsed) ? null : parsed;
    const bounded = nextValue == null
      ? null
      : Math.min(MAX_CLAIMED_TASKS_PER_USER_LIMIT, Math.max(1, Math.trunc(nextValue)));
    setMaxClaimedTasksDraft(bounded == null ? '' : String(bounded));
    setMaxClaimedTasksPending(true);
    try {
      await onSetMaxClaimedTasksPerUser(bounded);
    } finally {
      setMaxClaimedTasksPending(false);
    }
  };

  const settingRow = (children: React.ReactNode) => (
    <div className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">{children}</div>
  );

  const toggleClass = (active: boolean) => [
    'inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md border px-3 text-xs font-medium disabled:opacity-50',
    active
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
  ].join(' ');

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Claiming</h3>
        <div className="mt-3 space-y-4">
          {settingRow(
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Require first approval before more claims</p>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Experts can claim one task in this campaign, then must get a task approved before claiming another.
                </p>
              </div>
              <button
                type="button"
                onClick={() => void toggle()}
                disabled={pending}
                aria-pressed={enabled}
                className={toggleClass(enabled)}
              >
                {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {enabled ? 'On' : 'Off'}
              </button>
            </div>,
          )}
          {settingRow(
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Max tasks per user</p>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Stop experts from claiming another task after they own this many tasks in the campaign across any status. Leave blank for no cap.
                </p>
                <label className="mt-3 flex max-w-xs items-center gap-2 text-sm text-gray-700">
                  <span className="shrink-0">Task cap</span>
                  <input
                    type="number"
                    min={1}
                    max={MAX_TASKS_PER_USER_LIMIT}
                    step={1}
                    value={maxTasksDraft}
                    onChange={(e) => setMaxTasksDraft(e.target.value)}
                    onBlur={() => void commitMaxTasksPerUser()}
                    disabled={maxTasksPending}
                    placeholder="No cap"
                    className="h-9 w-28 rounded-md border border-gray-300 px-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
                  />
                </label>
              </div>
              {maxTasksPending && <Loader2 className="mt-2 h-3.5 w-3.5 animate-spin text-gray-500" />}
            </div>,
          )}
          {settingRow(
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Max tasks for trusted experts</p>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Total task cap for experts marked Trusted in this campaign. Leave blank to use the normal task cap.
                </p>
                <label className="mt-3 flex max-w-xs items-center gap-2 text-sm text-gray-700">
                  <span className="shrink-0">Task cap</span>
                  <input
                    type="number"
                    min={1}
                    max={MAX_TASKS_PER_USER_LIMIT}
                    step={1}
                    aria-label="Max tasks for trusted experts"
                    value={maxTrustedTasksDraft}
                    onChange={(e) => setMaxTrustedTasksDraft(e.target.value)}
                    onBlur={() => void commitMaxTasksPerTrustedUser()}
                    disabled={maxTrustedTasksPending}
                    placeholder="Use normal cap"
                    className="h-9 w-28 rounded-md border border-gray-300 px-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
                  />
                </label>
                <div className="mt-3 text-sm text-gray-600">
                  <p className="font-medium text-gray-700">Trusted experts</p>
                  {trustedUsers.length > 0 ? (
                    <ul className="mt-1 space-y-1" aria-label="Trusted experts">
                      {trustedUsers.map((user) => (
                        <li key={user.uid} className="break-words">
                          {user.email || `${user.displayName || user.uid} (no email address)`}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1">No trusted experts in this campaign.</p>
                  )}
                </div>
              </div>
              {maxTrustedTasksPending && <Loader2 className="mt-2 h-3.5 w-3.5 animate-spin text-gray-500" />}
            </div>,
          )}
          {settingRow(
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Max claimed tasks per user</p>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Stop experts from claiming another task when they already hold this many claimed or in-progress tasks in the campaign. Existing claims are not released. Leave blank for no cap.
                </p>
                <label className="mt-3 flex max-w-xs items-center gap-2 text-sm text-gray-700">
                  <span className="shrink-0">Claim cap</span>
                  <input
                    type="number"
                    min={1}
                    max={MAX_CLAIMED_TASKS_PER_USER_LIMIT}
                    step={1}
                    value={maxClaimedTasksDraft}
                    onChange={(e) => setMaxClaimedTasksDraft(e.target.value)}
                    onBlur={() => void commitMaxClaimedTasksPerUser()}
                    disabled={maxClaimedTasksPending}
                    placeholder="No cap"
                    className="h-9 w-28 rounded-md border border-gray-300 px-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
                  />
                </label>
              </div>
              {maxClaimedTasksPending && <Loader2 className="mt-2 h-3.5 w-3.5 animate-spin text-gray-500" />}
            </div>,
          )}
        </div>
      </section>

      <section className="mt-6 border-t border-gray-200 pt-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Review</h3>
        <div className="mt-3 space-y-4">
          {settingRow(
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Review routing</p>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Send submissions from every task type straight to Approved instead of the review queue.
                </p>
              </div>
              <button
                type="button"
                onClick={() => void toggleAutoApprove()}
                disabled={autoApprovePending}
                aria-pressed={autoApproveEnabled}
                aria-label="Review routing"
                className={toggleClass(autoApproveEnabled)}
              >
                {autoApprovePending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {autoApproveEnabled ? 'On' : 'Off'}
              </button>
            </div>,
          )}
          {settingRow(
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Submission email</p>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Send a short acknowledgement email when an expert submits or resubmits any task in this campaign.
                </p>
              </div>
              <button
                type="button"
                onClick={() => void toggleSubmissionEmail()}
                disabled={submissionEmailPending}
                aria-pressed={submissionEmailEnabled}
                aria-label="Submission email"
                className={toggleClass(submissionEmailEnabled)}
              >
                {submissionEmailPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {submissionEmailEnabled ? 'On' : 'Off'}
              </button>
            </div>,
          )}
          {settingRow(
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <label htmlFor="campaign-escalation-admin" className="text-sm font-medium text-gray-900">
                  Escalation admin
                </label>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Escalated reviews are assigned to this admin and the reviewer's note is emailed to them.
                </p>
              </div>
              <SearchableDropdown
                id="campaign-escalation-admin"
                options={[
                  { value: '', label: 'Not configured' },
                  ...(campaign.escalationAdminId
                    && !adminUsers.some((admin) => admin.uid === campaign.escalationAdminId)
                    ? [{
                      value: campaign.escalationAdminId,
                      label: 'Configured admin unavailable',
                      disabled: true,
                    }]
                    : []),
                  ...adminUsers.map((admin) => ({
                    value: admin.uid,
                    label: `${admin.displayName || admin.email || admin.uid}${admin.displayName && admin.email ? ` (${admin.email})` : ''}`,
                  })),
                ]}
                value={campaign.escalationAdminId ?? ''}
                onChange={(value) => void commitEscalationAdmin(value ?? '')}
                disabled={escalationPending}
                ariaLabel="Escalation admin"
                placeholder="Not configured"
                searchPlaceholder="Search admins"
                preserveOrder
                className="!w-auto min-w-64 shrink-0"
                triggerClassName="h-9 min-h-[36px] px-3 text-sm text-gray-700"
              />
            </div>,
          )}
        </div>
      </section>

      <section className="mt-6 border-t border-gray-200 pt-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Automatic release</h3>
        <div className="mt-3 space-y-4">
          {settingRow(
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Release untouched stale claims</p>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Return tasks to available when they have stayed claimed for more than the configured days, never entered in progress, and have no comments.
                </p>
                <label className="mt-3 flex max-w-xs items-center gap-2 text-sm text-gray-700">
                  <span className="shrink-0">Days claimed</span>
                  <input
                    type="number"
                    min={AUTO_RELEASE_STALE_CLAIMS_MIN_DAYS}
                    max={AUTO_RELEASE_STALE_CLAIMS_MAX_DAYS}
                    step={1}
                    value={daysDraft}
                    onChange={(e) => setDaysDraft(e.target.value)}
                    onBlur={() => void commitAutoRelease(releaseEnabled)}
                    disabled={releasePending}
                    className="h-9 w-24 rounded-md border border-gray-300 px-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={() => void commitAutoRelease(!releaseEnabled)}
                disabled={releasePending}
                aria-pressed={releaseEnabled}
                className={toggleClass(releaseEnabled)}
              >
                {releasePending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {releaseEnabled ? 'On' : 'Off'}
              </button>
            </div>,
          )}
          {settingRow(
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Release stale in-progress tasks</p>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Return tasks to available when they have not been edited for more than the configured days. This discards the current work and comments.
                </p>
                <label className="mt-3 flex max-w-xs items-center gap-2 text-sm text-gray-700">
                  <span className="shrink-0">Days since edit</span>
                  <input
                    type="number"
                    min={AUTO_RELEASE_STALE_IN_PROGRESS_MIN_DAYS}
                    max={AUTO_RELEASE_STALE_IN_PROGRESS_MAX_DAYS}
                    step={1}
                    value={inProgressDaysDraft}
                    onChange={(e) => setInProgressDaysDraft(e.target.value)}
                    onBlur={() => void commitAutoReleaseInProgress(inProgressReleaseEnabled)}
                    disabled={inProgressReleasePending}
                    className="h-9 w-24 rounded-md border border-gray-300 px-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={() => void commitAutoReleaseInProgress(!inProgressReleaseEnabled)}
                disabled={inProgressReleasePending}
                aria-pressed={inProgressReleaseEnabled}
                className={toggleClass(inProgressReleaseEnabled)}
              >
                {inProgressReleasePending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {inProgressReleaseEnabled ? 'On' : 'Off'}
              </button>
            </div>,
          )}
          {settingRow(
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Release inactive reviews</p>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Return assigned reviews to the review queue after this many hours without saved task changes, comments, or review activity. Submissions and comments are preserved. Checked every 15 minutes.
                </p>
                <label className="mt-3 flex max-w-xs items-center gap-2 text-sm text-gray-700">
                  <span className="shrink-0">Hours inactive</span>
                  <input
                    type="number"
                    min={AUTO_RELEASE_STALE_REVIEWS_MIN_HOURS}
                    max={AUTO_RELEASE_STALE_REVIEWS_MAX_HOURS}
                    step={1}
                    value={reviewHoursDraft}
                    onChange={(e) => setReviewHoursDraft(e.target.value)}
                    onBlur={() => void commitAutoReleaseReviews(reviewReleaseEnabled)}
                    disabled={reviewReleasePending}
                    className="h-9 w-24 rounded-md border border-gray-300 px-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={() => void commitAutoReleaseReviews(!reviewReleaseEnabled)}
                disabled={reviewReleasePending}
                aria-label="Release inactive reviews"
                aria-pressed={reviewReleaseEnabled}
                className={toggleClass(reviewReleaseEnabled)}
              >
                {reviewReleasePending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {reviewReleaseEnabled ? 'On' : 'Off'}
              </button>
            </div>,
          )}
        </div>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  valueText,
  loading,
  accent,
  icon,
}: {
  label: string;
  value?: number;
  valueText?: string;
  loading: boolean;
  accent: string;
  icon?: React.ReactNode;
}) {
  return (
    <div>
      <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {icon}
        {label}
      </p>
      <p className={`mt-1 text-lg font-semibold sm:text-xl ${accent}`}>
        {loading ? (
          <span className="text-gray-300">—</span>
        ) : valueText !== undefined ? (
          valueText
        ) : value === undefined ? (
          <span className="text-gray-300">—</span>
        ) : (
          value.toLocaleString()
        )}
      </p>
    </div>
  );
}

interface ExpertListProps {
  expertSort: ExpertSort;
  onSort: (key: ExpertSortKey) => void;
  campaign: Campaign;
  matchedUsers: User[];
  filteredUsers: User[];
  userQuery: string;
  onUserQueryChange: (q: string) => void;
  userStats: Map<string, CampaignUserTaskStats> | null;
  userStatsLoading: boolean;
  onAssign: (targetUid: string, req: AssignTaskRequest) => Promise<unknown>;
  pendingAssign: boolean;
}

function ExpertList({
  expertSort,
  onSort,
  campaign,
  matchedUsers,
  filteredUsers,
  userQuery,
  onUserQueryChange,
  userStats,
  userStatsLoading,
  onAssign,
  pendingAssign,
}: ExpertListProps) {
  const [assignUser, setAssignUser] = useState<User | null>(null);
  const totalActive = campaign.activeExpertIds.length;
  const totalAligned = campaign.alignedCampaignExpertStatuses
    ? Object.keys(campaign.alignedCampaignExpertStatuses).length
    : totalActive;
  const matchedCount = matchedUsers.length;
  const filterActive = userQuery.trim().length > 0;
  const sortedUsers = sortExperts(filteredUsers, campaign, userStats, expertSort);

  return (
    <section>
      <SectionHeader
        title="Experts"
        subtitle={
          totalAligned === 0
            ? 'No experts on this campaign yet.'
            : `${matchedCount} of ${totalAligned} expert${
                totalAligned === 1 ? '' : 's'
              } ${matchedCount === 1 ? 'has' : 'have'} a Workbench account.`
        }
      />

      {totalAligned === 0 ? (
        <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
          Experts come from the Aligned sync. They'll show up here after the next sync.
        </div>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:min-w-[240px] sm:max-w-md sm:flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={userQuery}
                onChange={(e) => onUserQueryChange(e.target.value)}
                placeholder="Filter by name or email…"
                aria-label="Filter experts"
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {userQuery && (
                <button
                  type="button"
                  onClick={() => onUserQueryChange('')}
                  aria-label="Clear filter"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <span className="text-xs text-gray-500">
              {filterActive
                ? `${filteredUsers.length} of ${matchedCount} match`
                : `${matchedCount} expert${matchedCount === 1 ? '' : 's'}`}
            </span>
          </div>

          {matchedCount === 0 ? (
            <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
              None of the {totalAligned} experts have a Workbench account yet.
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
              No experts match "{userQuery}".
            </div>
          ) : (
            <>
              <div className="mt-3 hidden overflow-x-auto rounded-md border border-gray-200 bg-white md:block">
                <ExpertTable
                  users={sortedUsers}
                  sort={expertSort}
                  onSort={onSort}
                  campaign={campaign}
                  userStats={userStats}
                  userStatsLoading={userStatsLoading}
                  onAssignUser={setAssignUser}
                />
              </div>
              <ul className="mt-3 grid grid-cols-1 gap-3 md:hidden">
                {sortedUsers.map((u) => (
                  <ExpertCard
                    key={u.uid}
                    user={u}
                    campaignId={campaign.id}
                    alignedCampaignStatus={alignedStatusForUser(campaign, u)}
                    stats={userStats?.get(u.uid) ?? null}
                    statsLoading={userStatsLoading}
                    onAssign={() => setAssignUser(u)}
                  />
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {assignUser && (
        <AssignTaskModal
          campaign={campaign}
          user={assignUser}
          busy={pendingAssign}
          onClose={() => setAssignUser(null)}
          onAssign={async (req) => {
            await onAssign(assignUser.uid, req);
          }}
        />
      )}
    </section>
  );
}

function ExpertTable({
  sort,
  onSort,
  users,
  campaign,
  userStats,
  userStatsLoading,
  onAssignUser,
}: {
  sort: ExpertSort;
  onSort: (key: ExpertSortKey) => void;
  users: User[];
  campaign: Campaign;
  userStats: Map<string, CampaignUserTaskStats> | null;
  userStatsLoading: boolean;
  onAssignUser: (user: User) => void;
}) {
  return (
    <table className="min-w-full divide-y divide-gray-200 text-sm">
      <thead className="bg-gray-50">
        <tr>
          {([
            ['expert', 'Expert'], ['status', 'Aligned status'], ['total', 'Total'],
            ['completed', 'Done'], ['inReview', 'Review'], ['rework', 'Rework'],
            ['inProgress', 'In progress'], ['claimed', 'Claimed'],
          ] as const).map(([key, label]) => (
            <ExpertTableHeader
              key={key}
              label={label}
              align={key === 'expert' || key === 'status' ? 'left' : 'right'}
              direction={sort.key === key ? sort.direction : undefined}
              onSort={() => onSort(key)}
            />
          ))}
          <ExpertTableHeader label="" align="right" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {users.map((user) => (
          <ExpertTableRow
            key={user.uid}
            user={user}
            campaign={campaign}
            stats={userStats?.get(user.uid) ?? null}
            statsLoading={userStatsLoading}
            onAssign={() => onAssignUser(user)}
          />
        ))}
      </tbody>
    </table>
  );
}

function ExpertTableHeader({
  label,
  align = 'left',
  direction,
  onSort,
}: {
  label: string;
  align?: 'left' | 'right';
  direction?: 'ascending' | 'descending';
  onSort?: () => void;
}) {
  const Icon = direction === 'ascending' ? ArrowUp : direction === 'descending' ? ArrowDown : ArrowUpDown;
  return (
    <th
      scope="col"
      aria-sort={onSort ? direction ?? 'none' : undefined}
      className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {onSort ? (
        <button
          type="button"
          onClick={onSort}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        >
          {label}
          <Icon aria-hidden="true" className={`h-3.5 w-3.5 ${direction ? 'text-primary' : 'text-gray-400'}`} />
        </button>
      ) : label}
    </th>
  );
}

function ExpertTableRow({
  user,
  campaign,
  stats,
  statsLoading,
  onAssign,
}: {
  user: User;
  campaign: Campaign;
  stats: CampaignUserTaskStats | null;
  statsLoading: boolean;
  onAssign: () => void;
}) {
  const resolved = stats ?? { claimed: 0, inProgress: 0, rework: 0, inReview: 0, completed: 0 };
  const total = totalCampaignUserTaskStats(resolved);
  const status = alignedStatusForUser(campaign, user);

  return (
    <tr className="hover:bg-gray-50">
      <td className="max-w-[260px] px-4 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="truncate font-medium text-gray-900">{user.displayName || '(no name)'}</p>
          {user.aiDetectionTopWarningQuartile && <AiDetectionWarningPill />}
        </div>
        <p className="truncate text-xs text-gray-500">{user.email || '(no email)'}</p>
      </td>
      <td className="px-4 py-3">
        {status ? <AlignedCampaignStatusPill status={status} /> : <span className="text-xs text-gray-400">-</span>}
      </td>
      <ExpertCountCell value={total} loading={statsLoading} accent="font-semibold text-gray-900" />
      <ExpertCountCell value={resolved.completed} loading={statsLoading} accent="text-emerald-600" />
      <ExpertCountCell value={resolved.inReview} loading={statsLoading} accent="text-blue-600" />
      <ExpertCountCell value={resolved.rework} loading={statsLoading} accent="text-rose-600" />
      <ExpertCountCell value={resolved.inProgress} loading={statsLoading} accent="text-violet-600" />
      <ExpertCountCell value={resolved.claimed} loading={statsLoading} accent="text-amber-600" />
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onAssign}
            className="text-sm font-medium text-primary hover:underline"
          >
            Assign
          </button>
          <BrowseTasksLink campaignId={campaign.id} user={user} />
        </div>
      </td>
    </tr>
  );
}

function ExpertCountCell({
  value,
  loading,
  accent,
}: {
  value: number;
  loading: boolean;
  accent: string;
}) {
  return (
    <td className={`px-4 py-3 text-right tabular-nums ${accent}`}>
      {loading ? <span className="text-gray-300">-</span> : value.toLocaleString()}
    </td>
  );
}

function alignedStatusForUser(campaign: Campaign, user: User): string | null {
  if (!user.expertId) return null;
  return campaign.alignedCampaignExpertStatuses?.[user.expertId] ?? 'active';
}

function ExpertCard({
  user,
  campaignId,
  alignedCampaignStatus,
  stats,
  statsLoading,
  onAssign,
}: {
  user: User;
  campaignId: string;
  alignedCampaignStatus: string | null;
  stats: CampaignUserTaskStats | null;
  statsLoading: boolean;
  onAssign: () => void;
}) {
  const resolved = stats ?? { claimed: 0, inProgress: 0, rework: 0, inReview: 0, completed: 0 };
  const total = totalCampaignUserTaskStats(resolved);

  return (
    <li className="flex flex-col gap-3 rounded-md border border-gray-200 bg-white p-4">
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="truncate text-sm font-medium text-gray-900">
            {user.displayName || '(no name)'}
          </p>
          {user.aiDetectionTopWarningQuartile && <AiDetectionWarningPill />}
          {alignedCampaignStatus && (
            <AlignedCampaignStatusPill status={alignedCampaignStatus} />
          )}
        </div>
        <p className="truncate text-xs text-gray-500">{user.email || '(no email)'}</p>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center sm:grid-cols-6">
        <MiniStat
          label="Total"
          value={total}
          loading={statsLoading}
          accent="text-gray-900 font-semibold"
        />
        <MiniStat
          label="Done"
          value={resolved.completed}
          loading={statsLoading}
          accent="text-emerald-600"
        />
        <MiniStat
          label="Review"
          value={resolved.inReview}
          loading={statsLoading}
          accent="text-blue-600"
        />
        <MiniStat
          label="Rework"
          value={resolved.rework}
          loading={statsLoading}
          accent="text-rose-600"
        />
        <MiniStat
          label="Progress"
          value={resolved.inProgress}
          loading={statsLoading}
          accent="text-violet-600"
        />
        <MiniStat
          label="Claimed"
          value={resolved.claimed}
          loading={statsLoading}
          accent="text-amber-600"
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onAssign}
          className="inline-flex items-center justify-center gap-1 self-start rounded-md border border-primary bg-white px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/5"
        >
          Assign task
        </button>
        <Link
          to={browseTasksUrl(campaignId, user)}
          className="inline-flex items-center justify-center gap-1 self-start rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          Browse tasks
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </li>
  );
}

function BrowseTasksLink({ campaignId, user }: { campaignId: string; user: User }) {
  return (
    <Link
      to={browseTasksUrl(campaignId, user)}
      className="inline-flex items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
    >
      Browse
      <ArrowRight className="h-3 w-3" />
    </Link>
  );
}

function browseTasksUrl(campaignId: string, user: User): string {
  return paths.browseTasks(buildBrowseTasksSearchParams({
    campaignId,
    ownerUid: user.uid,
    ownerEmail: user.email ?? '',
    autorun: true,
  }));
}

function AlignedCampaignStatusPill({ status }: { status: string }) {
  const label = formatAlignedCampaignStatus(status);
  const cls = alignedCampaignStatusClass(status);
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      Aligned: {label}
    </span>
  );
}

function formatAlignedCampaignStatus(status: string): string {
  if (!status) return 'Unknown';
  return status
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function alignedCampaignStatusClass(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
    case 'training':
    case 'onboarding':
      return 'bg-blue-50 text-blue-700 ring-1 ring-blue-200';
    case 'assessment':
    case 'qualification':
      return 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200';
    case 'onHold':
      return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
    case 'inactive':
    case 'offboarding':
      return 'bg-gray-100 text-gray-700 ring-1 ring-gray-200';
    default:
      return 'bg-slate-100 text-slate-700 ring-1 ring-slate-200';
  }
}

function MiniStat({
  label,
  value,
  loading,
  accent,
}: {
  label: string;
  value: number;
  loading: boolean;
  accent: string;
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`text-sm ${accent}`}>
        {loading ? <span className="text-gray-300">—</span> : value.toLocaleString()}
      </p>
    </div>
  );
}
